/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// =================================================================================================
// Store Card Aria Labels
// Updates aria-label for "Get directions" and "View store details" links in store cards.
// Class names are auto-generated, so selectors use partial class matching and href patterns.
// =================================================================================================

function updateStoreCardAriaLabels() {
  $('[class*="ai-store-card-"]').filter(function () {
    return !$(this).parents('[class*="ai-store-card-"]').length;
  }).each(function () {
    const $card = $(this);

    const city = $card.find('h3').first().text().trim();

    $card.find('a[href*="maps.google.com"]').attr(
      'aria-label',
      `Get directions - ${city}`
    );

    $card.find('a[href*="/pages/"]').attr(
      'aria-label',
      `View store details - ${city}`
    );
  });
}

// =================================================================================================
// Recognition Iframe Title
// Adds a descriptive title to the recognition iframe for accessibility.
// =================================================================================================

function updateIframeTitle() {
  $('[data-testid="recognition-iframe"]').attr('title', 'Recognition');
}

// =================================================================================================
// Init
// =================================================================================================

document.addEventListener('DOMContentLoaded', () => {
  console.log("A11Y Start");
  updateStoreCardAriaLabels();
  updateIframeTitle();

function addRoleNoneToMinificationBtn() {
    document
        .querySelectorAll('li.minification-btn.swatch-minification.btn-with-count')
        .forEach(li => {
            if (li.children.length > 0 && !li.hasAttribute('role')) {
                li.setAttribute('role', 'none');
            }
        });
}

addRoleNoneToMinificationBtn();

const observer = new MutationObserver(addRoleNoneToMinificationBtn);

observer.observe(document.body, {
    childList: true,
    subtree: true
});

document.querySelectorAll('a').forEach(anchor => {
    const content = anchor.innerHTML
        .replace(/<br\s*\/?>/gi, '')
        .replace(/&nbsp;/gi, '')
        .trim();

    if (content === '') {
        const span = document.createElement('span');

        [...anchor.attributes].forEach(attr => {
            if (attr.name !== 'href') {
                span.setAttribute(attr.name, attr.value);
            }
        });

        span.innerHTML = anchor.innerHTML;
        anchor.replaceWith(span);
    }
});

setTimeout(() => {
  document.querySelectorAll('.lp99_wte-section').forEach(section => {
    const title = section.querySelector('.lp99_section-title')?.textContent.trim();
    const button = section.querySelector('.lp99_wte-section-arrow-class');

    if (title && button) {
        button.setAttribute('title', title);
        button.setAttribute('aria-label', title);
    }
});
$('#referral-hero-section-section [name="subscribe"]').attr("aria-label", "Subscribe for Future Update");
$(".jdgm-cards-carousel .jdgm-cards-wrapper .jdgm-media-card img").attr("alt", "");
 $('.jdgm-rev-widg__summary-stars').each(function () {
        var $container = $(this);

        var ariaLabel = $container.attr('aria-label');
        var role = $container.attr('role');

        // Remove from parent
        $container.removeAttr('aria-label role');

        // Add to first star
        $container.find('.jdgm-star').first().attr({
            'aria-label': ariaLabel,
            'role': role
        });

        // Hide remaining stars from screen readers
        $container.find('.jdgm-star').not(':first').attr('aria-hidden', 'true');
    });

(function () {
    function updateAccessibilityFixes() {

        // Judge.me review links
        document.querySelectorAll('.jdgm-rev-snippet-card').forEach(function (card) {
            const reviewer = card.querySelector('.jdgm-rev-snippet-card__reviewer');
            const reviewLink = card.querySelector('.jdgm-rev-snippet-card__full-review-link');

            if (reviewer && reviewLink) {
                reviewLink.setAttribute(
                    'aria-label',
                    'Read full review by ' + reviewer.textContent.trim()
                );
            }
        });

        // Klaviyo product image alt text
        document.querySelectorAll('[data-testid="hub-widget-container"]').forEach(function (widget) {
            const productName = widget.querySelector('[data-testid="name"]')?.textContent?.trim();
            const productImage = widget.querySelector('img[data-testid="product-image"]');

            if (productName && productImage) {
                productImage.setAttribute('alt', productName);
            }
        });
    }

    updateAccessibilityFixes();

    const observer = new MutationObserver(function () {
        updateAccessibilityFixes();
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });
})();

$(".jdgm-review-snippet-widget > i").attr({"role":"button", "tabindex":"0"});

function applyAlt() {
  document.querySelectorAll('.kl-hub-card').forEach(card => {
    const titleEl = card.querySelector('[data-testid="name"] a');
    const img = card.querySelector('img[data-testid="product-image"]');

    if (!titleEl || !img) return;

    img.setAttribute('alt', titleEl.textContent.trim());
  });
}

applyAlt();

const observer = new MutationObserver(applyAlt);

observer.observe(document.body, {
  childList: true,
  subtree: true
});
}, 2000);
  

$(".main-search__form input.main-search__input").removeAttr("aria-expanded");
$("#HeaderSearchResults").removeAttr("role");
$(".page-shade").attr("role", "button");

$(".shopify-section-group-footer-group .section-footer__menu-block").removeAttr("role");
$(".shopify-section-group-footer-group .section-footer__menu-block").removeAttr("aria-label");

  console.log("A11Y End");
});